import type { ReactElement } from "react"
import { Link } from "react-router"
import styled from "styled-components"
import { RemoteImage } from "~/shared/ui"
import type { DetailSession } from "../types"

/** Figma's session artwork is 545 × 320. */
const THUMBNAIL_RATIO = 545 / 320
const DESCRIPTION_LINE_LIMIT = 4

/**
 * Capped below the rail width so the next card always peeks in — Figma shows a
 * partial card at both ends, and on a phone that sliver is the only cue that the
 * rail scrolls.
 */
const Root = styled.li`
  width: min(${({ theme }) => theme.detail.sessionCardWidth}, 85%);
  flex: 0 0 auto;
  scroll-snap-align: start;
`

const CardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  text-decoration: none;
  color: ${({ theme }) => theme.color.textPrimary};
  border-radius: ${({ theme }) => theme.radius.md};
`

const Artwork = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 0.35);

  /* Figma dims the artwork on hover; the title underlines to make the affordance
     survive at any contrast. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: ${({ theme }) => theme.radius.md};
    background-color: transparent;
    transition: background-color 120ms ease-in-out;
  }

  ${CardLink}:hover &::after,
  ${CardLink}:focus-visible &::after {
    background-color: ${({ theme }) => theme.color.imageScrim};
  }
`

const DurationChip = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.spacing.xs};
  bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.spacing.xxs} ${theme.spacing.xs}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.color.chipSurface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  z-index: 1;
`

const ProgressTrack = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.spacing.xs};
  bottom: ${({ theme }) => theme.spacing.xs};
  left: ${({ theme }) => theme.spacing.xs};
  height: 4px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.progressTrack};
  z-index: 1;
`

const ProgressFill = styled.div<{ $percent: number }>`
  width: ${({ $percent }) => `${$percent}%`};
  height: 100%;
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme }) => theme.color.accent};
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
`

const Title = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  overflow-wrap: anywhere;

  ${CardLink}:hover &,
  ${CardLink}:focus-visible & {
    text-decoration: underline;
  }
`

const Description = styled.p`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${DESCRIPTION_LINE_LIMIT};
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  color: ${({ theme }) => theme.color.textSecondary};
`

interface SessionCardProps {
  readonly session: DetailSession
  readonly qaHook: string
}

export const SessionCard = ({ session, qaHook }: SessionCardProps): ReactElement => (
  <Root>
    <CardLink to={session.href} data-qa-hook={qaHook}>
      <Artwork>
        <RemoteImage src={session.thumbnailUrl} alt="" ratio={THUMBNAIL_RATIO} qaHook={`${qaHook}-image`} />
        {session.duration ? <DurationChip data-qa-hook={`${qaHook}-duration`}>{session.duration}</DurationChip> : null}
        {session.progressPercent > 0 ? (
          <ProgressTrack
            role="progressbar"
            aria-label={`${session.title} progress`}
            aria-valuenow={session.progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            data-qa-hook={`${qaHook}-progress`}
          >
            <ProgressFill $percent={session.progressPercent} />
          </ProgressTrack>
        ) : null}
      </Artwork>

      <Content>
        <Title>{session.title}</Title>
        {session.description ? <Description>{session.description}</Description> : null}
      </Content>
    </CardLink>
  </Root>
)
